/**
 * Peer lifecycle state store — PR-1 of docs/connection-state-machine.md.
 *
 * Why this exists:
 *   The legacy "is this peer working?" derivation reads
 *   `RTCPeerConnection.connectionState` + `iceConnectionState` + heartbeat
 *   probes + ad-hoc flags scattered across `provider.tsx`. There is no
 *   single source of truth, no event stream of transitions, and no way for
 *   downstream features (groups star meshing, multisource chunk routing,
 *   AV stream topology) to make eligibility decisions without re-deriving.
 *
 *   This store fixes the classification gap underneath. It subscribes to
 *   each peer's RTC + channel events, runs `derivePeerLifecycleState()` on
 *   every fire, and emits typed events when the projected lifecycle or
 *   modifiers change. PR-1 is OBSERVATION-ONLY — UI continues reading via
 *   the legacy `usePeerUIState` hook, transport logic continues branching
 *   on raw RTC state. PR-2 swaps consumers; PR-3+ consumes events for
 *   transport policy.
 *
 * Design choices (see Q11.A in the design doc):
 *   - Event-driven rather than polled. RTC + channel listeners cover
 *     ≥90% of state-relevant transitions. Mutation sites that don't fire
 *     RTC events (offlinePeers.add/delete, wakeDormant) are followed
 *     within milliseconds-to-seconds by RTC events that DO fire — fine for
 *     observation-only. PR-3 will add explicit reclassify() at those sites
 *     for tighter latency once the store is wired into transport policy.
 *   - Listeners attached via `addEventListener` rather than `onXXX = fn`
 *     so the existing handler-reassignment patterns in `provider.tsx` do
 *     not clobber our hooks.
 *   - Snapshots are in-memory only — see Q10. State is a derived projection
 *     of live transport, not worth persisting; on reload `RTCPeerConnection`
 *     itself is gone, so we'd be reconstructing from fresh signaling anyway.
 */

import { EventEmitter } from "events";

import { connectionDiagnostics } from "@/lib/connection-diagnostics";
import {
  derivePeerLifecycleState,
  type PeerLifecycleState,
  type PeerModifiers,
  type PeerStateSnapshot,
  type RetryPhase,
} from "@/lib/peer-ui-state";
import {
  extensionCapabilityService,
  type MemoryPressureState,
} from "@/services/extension-capability-service";

// ─── Event types (Q11) ───────────────────────────────────────────────────────

export interface PeerStateChangeEvent {
  type: "peer:state-change";
  peerId: string;
  /** undefined on first classification (initial attach). */
  from: PeerLifecycleState | undefined;
  to: PeerLifecycleState;
  reason: string;
  timestamp: number;
}

export interface PeerModifierChangeEvent {
  type: "peer:modifier-change";
  peerId: string;
  modifier: keyof PeerModifiers;
  value: boolean | readonly string[];
  reason: string;
  timestamp: number;
}

export interface PeerLifecycleEndEvent {
  type: "peer:lifecycle-end";
  peerId: string;
  reason: string;
  timestamp: number;
}

export type PeerStoreEvent =
  | PeerStateChangeEvent
  | PeerModifierChangeEvent
  | PeerLifecycleEndEvent;

type PeerStoreEventMap = {
  "peer:state-change": [PeerStateChangeEvent];
  "peer:modifier-change": [PeerModifierChangeEvent];
  "peer:lifecycle-end": [PeerLifecycleEndEvent];
};

// ─── Minimal MobyPeer-ish shape ──────────────────────────────────────────────
//
// We declare the narrowest interface the store needs so the store is
// unit-testable without dragging in `provider.tsx`. `MobyPeer` itself
// already satisfies this shape — see PR-1 integration map in the design
// doc.

export interface ChannelLike {
  channel: { readyState: RTCDataChannelState } | null;
  on(event: string, listener: (...args: any[]) => void): unknown;
  off(event: string, listener: (...args: any[]) => void): unknown;
}

export interface PeerLike {
  address: string;
  connection: RTCPeerConnection;
  chat: ChannelLike;
  file?: ChannelLike;
  retryPhase?: RetryPhase;
  reconnectionAttempts?: number;
  isContactDeleted?: boolean;
  closed?: boolean;
  node?: { offlinePeers?: Set<string> };
  /**
   * Per-peer in-flight transfer registry. We only read `.size` — the value
   * shape is intentionally unknown so the lifecycle store doesn't need to
   * know about `FileTransferRecord`. Source: `MobyPeer.activeTransfers`.
   */
  activeTransfers?: { readonly size: number };
}

// ─── Store ───────────────────────────────────────────────────────────────────

interface AttachHandles {
  peer: PeerLike;
  listeners: {
    onIce: () => void;
    onConn: () => void;
    onChatOpen: () => void;
    onChatClose: () => void;
    onFileOpen: () => void;
    onFileClose: () => void;
  };
}

export class PeerStateStore {
  private snapshots = new Map<string, PeerStateSnapshot>();
  private emitter: EventEmitter<PeerStoreEventMap> = new EventEmitter();
  private handles = new Map<string, AttachHandles>();

  /**
   * Node-level throttle state. Sourced from `extensionCapabilityService`
   * pressure transitions; fans out to every peer's `throttled` modifier on
   * each reclassify call. Pressure is a node-level signal (OPFS quota,
   * heap usage) that affects all peers identically, so we cache it on the
   * store rather than per-peer.
   */
  private globalThrottled = false;

  /**
   * Per-peer connection-type cache. Populated by the relay-poll timer
   * (PR-2) which calls `peer.connection.getStats()` and inspects the
   * selected candidate pair. Defaults to 'unknown' until the first poll
   * resolves; the classifier treats unknown as `connected` (relay flag
   * false) by default.
   */
  private connectionTypes = new Map<string, 'direct' | 'relay' | 'unknown'>();

  /**
   * One global poller (1s while any peer is busy, 5s while all are idle)
   * that calls reclassify on all attached peers. Catches
   * `activeTransferCount` mutations on `MobyPeer.activeTransfers` without
   * instrumenting the 50+ Map mutation sites — reclassify is a no-op when
   * nothing changed, so the cost is just a Map.get + classifier call per
   * peer per tick. The adaptive cadence keeps idle CPU low while still
   * surfacing localBusy modifier changes within ~1s once a transfer
   * starts.
   */
  private transferPollTimer: ReturnType<typeof setTimeout> | null = null;
  private static readonly TRANSFER_POLL_BUSY_MS = 1000;
  private static readonly TRANSFER_POLL_IDLE_MS = 5000;

  // Note: the dedicated 5s `statsPollTimer` was removed. `connectionType`
  // is now fed into the store from `MobyPeer.updateConnectionStats` via
  // `setConnectionType()` — that path already runs `getStats()` on its own
  // adaptive 5s/30s schedule, so we avoid duplicating the call (and the
  // associated network-thread lock) for every connected peer.

  /** Cleanup handle for the extensionCapabilityService pressure subscription. */
  private pressureUnsubscribe: (() => void) | null = null;

  constructor() {
    // Seed the throttle flag with whatever the service currently reports —
    // pressure events only fire on transitions, so without a seed we'd be
    // stuck at `false` until the next change.
    const initialPressure: MemoryPressureState =
      extensionCapabilityService.getCurrentPressureState();
    this.globalThrottled = initialPressure !== 'normal';

    // Subscribe for the lifetime of the store. The unsubscribe handle is
    // held so `_resetForTest` can detach cleanly between test cases.
    this.pressureUnsubscribe = extensionCapabilityService.onPressureChanged(
      (evt) => {
        const nextThrottled = evt.current !== 'normal';
        this.setGlobalThrottled(
          nextThrottled,
          `pressure:${evt.previous}->${evt.current}`,
        );
      },
    );
  }

  /**
   * Subscribe a peer's lifecycle to the store.
   *
   * Re-attach semantics: if a prior handle already exists for this address
   * (typically from a peer-replacement path where the caller disposed the
   * old MobyPeer and built a fresh one at the same address), the prior
   * handle is detached first and its listeners torn down. This prevents
   * the UI-badge-desync bug where the lifecycle store stays wired to a
   * disposed peer's last known state (the 5/3 W10 Edge PEER-RECOVERY
   * trace: old peer went `connected → failed`, new peer came up and
   * channels reopened, but the badge stayed gray forever because no
   * lifecycle event fired for the replacement).
   *
   * Callers that explicitly call `detach()` before `attach()` get the
   * same net behavior and a cleaner call graph — both patterns are
   * supported; the auto-detach is a defense-in-depth safety net.
   *
   * Side effects on (re-)attach:
   *   - Emits `peer:lifecycle-end` for the prior handle (if any).
   *   - Adds 2 listeners on `peer.connection` (ICE + connection state).
   *   - Adds 2 listeners on `peer.chat` (open/close).
   *   - Adds 2 listeners on `peer.file` if present (open/close).
   *   - Runs initial classification and emits a `peer:state-change` with
   *     `from: undefined`.
   */
  attach(peer: PeerLike): void {
    if (this.handles.has(peer.address)) {
      // Defense-in-depth: caller forgot to detach before re-attach, or is
      // simply calling attach() twice at the same address. Tear down the
      // stale handle so the new peer's events land in a fresh classifier.
      this.detach(peer.address, "reattach-replace");
    }

    const reclassify = (reason: string): void => {
      this.reclassify(peer, reason);
    };

    // RTC events on peer.connection — additive listeners (`addEventListener`
    // never collides with the `peer.connection.onXXX = fn` assignments in
    // peerConnectionLoop, which would otherwise overwrite our hooks).
    const onIce = () => reclassify("ice-connection-state-change");
    const onConn = () => reclassify("connection-state-change");
    peer.connection.addEventListener("iceconnectionstatechange", onIce);
    peer.connection.addEventListener("connectionstatechange", onConn);

    // MobyChannel emits open/close via Node's EventEmitter — independent of
    // the underlying RTCDataChannel (which can be swapped via setupChannel).
    const onChatOpen = () => reclassify("chat-channel-open");
    const onChatClose = () => reclassify("chat-channel-close");
    const onFileOpen = () => reclassify("file-channel-open");
    const onFileClose = () => reclassify("file-channel-close");
    peer.chat.on("open", onChatOpen);
    peer.chat.on("close", onChatClose);
    peer.file?.on("open", onFileOpen);
    peer.file?.on("close", onFileClose);

    this.handles.set(peer.address, {
      peer,
      listeners: { onIce, onConn, onChatOpen, onChatClose, onFileOpen, onFileClose },
    });

    // Initial classification fires `peer:state-change` with `from: undefined`.
    reclassify("attach");

    // First-attach side effect: spin up the global pollers if this is the
    // first peer. They auto-shut-down in detach() when the last peer leaves.
    this.ensurePollersRunning();
  }

  /**
   * Tear down all listeners for a peer and emit `peer:lifecycle-end`.
   * Idempotent — calling `detach` for an unknown peerId is a no-op.
   */
  detach(peerId: string, reason: string = "detach"): void {
    const handle = this.handles.get(peerId);
    if (!handle) return;

    try {
      handle.peer.connection.removeEventListener(
        "iceconnectionstatechange",
        handle.listeners.onIce,
      );
      handle.peer.connection.removeEventListener(
        "connectionstatechange",
        handle.listeners.onConn,
      );
      handle.peer.chat.off("open", handle.listeners.onChatOpen);
      handle.peer.chat.off("close", handle.listeners.onChatClose);
      handle.peer.file?.off("open", handle.listeners.onFileOpen);
      handle.peer.file?.off("close", handle.listeners.onFileClose);
    } catch (err) {
      // Listener cleanup during peer teardown — don't crash if the peer's
      // RTCPeerConnection or channels are already disposed. This path is
      // best-effort.
      console.warn("[PEER-STATE-STORE] detach: listener cleanup failed", {
        peerId: peerId.slice(0, 8),
        error: err,
      });
    }

    this.handles.delete(peerId);
    this.snapshots.delete(peerId);
    this.connectionTypes.delete(peerId);

    // Last peer left — stop the global pollers to avoid background work
    // when there's nothing to observe.
    if (this.handles.size === 0) {
      this.stopPollers();
    }

    this.emitter.emit("peer:lifecycle-end", {
      type: "peer:lifecycle-end",
      peerId,
      reason,
      timestamp: Date.now(),
    });
  }

  /**
   * External re-classification trigger. Used at mutation sites that don't
   * fire RTC events (e.g. `offlinePeers.add()`, `wakeDormant()`). PR-1
   * relies on the RTC + channel listeners to catch most transitions, so
   * this method is exported but currently unused inside provider.tsx —
   * PR-3 will start invoking it where event latency matters.
   */
  reclassify(peer: PeerLike, reason: string): void {
    const prev = this.snapshots.get(peer.address);
    // Pass the previous lifecycle to the classifier so the grace-blip rule
    // can disambiguate "degraded from connected" (collapse to connected)
    // from "degraded from connecting" (stay at connecting).
    const derived = derivePeerLifecycleState({
      ...this.buildClassifierInputs(peer),
      previousLifecycle: prev?.lifecycle,
    });
    const now = Date.now();

    const next: PeerStateSnapshot = {
      peerId: peer.address,
      lifecycle: derived.lifecycle,
      modifiers: derived.modifiers,
      lastTransitionAt: now,
      lastTransitionReason: `${reason} → ${derived.reason}`,
      uiStateRaw: derived.uiStateRaw,
    };

    if (!prev) {
      // First classification — always emit, with `from: undefined`.
      this.snapshots.set(peer.address, next);
      const evt: PeerStateChangeEvent = {
        type: "peer:state-change",
        peerId: peer.address,
        from: undefined,
        to: next.lifecycle,
        reason: next.lastTransitionReason,
        timestamp: next.lastTransitionAt,
      };
      this.emitter.emit("peer:state-change", evt);
      this.logToDiagnostics(evt);
      return;
    }

    const lifecycleChanged = prev.lifecycle !== next.lifecycle;
    const modifiersChanged = !modifiersEqual(prev.modifiers, next.modifiers);
    const uiStateRawChanged = prev.uiStateRaw !== next.uiStateRaw;

    if (!lifecycleChanged && !modifiersChanged && !uiStateRawChanged) {
      // True no-op — projected state is identical including the migration
      // shim. Skip both the snapshot write and event emission.
      return;
    }

    // Preserve transition timestamp if only modifiers/uiStateRaw changed —
    // `lastTransitionAt` should reflect when the lifecycle last changed,
    // not when modifier toggles or candidate-pair refinement happened.
    if (!lifecycleChanged) {
      next.lastTransitionAt = prev.lastTransitionAt;
    }

    // Always update the snapshot when anything in the derived state
    // differs, so reads of `uiStateRaw` reflect the latest classification.
    // Event emission below is gated on lifecycle/modifier changes only —
    // a `connected` → `connected-direct` refinement is a uiStateRaw-only
    // change and produces no event.
    this.snapshots.set(peer.address, next);

    if (!lifecycleChanged && !modifiersChanged) {
      // uiStateRaw-only change — snapshot updated above, no events fire.
      return;
    }

    if (lifecycleChanged) {
      const evt: PeerStateChangeEvent = {
        type: "peer:state-change",
        peerId: peer.address,
        from: prev.lifecycle,
        to: next.lifecycle,
        reason: next.lastTransitionReason,
        timestamp: now,
      };
      this.emitter.emit("peer:state-change", evt);
      this.logToDiagnostics(evt);
    }

    if (modifiersChanged) {
      // Emit one modifier-change event per flipped key. Consumers that only
      // care about a single modifier (e.g. relay) can subscribe and filter.
      const keys = Object.keys(next.modifiers) as Array<keyof PeerModifiers>;
      for (const key of keys) {
        const prevVal = prev.modifiers[key];
        const nextVal = next.modifiers[key];
        if (!modifierValueEqual(prevVal, nextVal)) {
          const evt: PeerModifierChangeEvent = {
            type: "peer:modifier-change",
            peerId: peer.address,
            modifier: key,
            value: nextVal,
            reason: next.lastTransitionReason,
            timestamp: now,
          };
          this.emitter.emit("peer:modifier-change", evt);
        }
      }
    }
  }

  /** Snapshot read API — see Q11 read API. */
  getPeer(peerId: string): PeerStateSnapshot | undefined {
    return this.snapshots.get(peerId);
  }

  /**
   * Returns a defensive copy of the snapshots map. The values are the live
   * snapshot references — callers should treat them as read-only.
   */
  getAllPeers(): Map<string, PeerStateSnapshot> {
    return new Map(this.snapshots);
  }

  /**
   * Subscribe to a typed event channel. Returns an unsubscribe function.
   *
   * Example:
   *   const unsub = peerStateStore.subscribe('peer:state-change', evt => {
   *     console.log(evt.peerId, evt.from, '→', evt.to);
   *   });
   */
  subscribe<E extends keyof PeerStoreEventMap>(
    type: E,
    listener: (event: PeerStoreEventMap[E][0]) => void,
  ): () => void {
    this.emitter.on(type, listener as never);
    return () => {
      this.emitter.off(type, listener as never);
    };
  }

  /**
   * Set the node-level throttle flag and reclassify all attached peers.
   * Called automatically by the pressure-event subscription, but exposed
   * publicly so tests and PR-3+ transport policy can drive it directly.
   *
   * Idempotent — same value is a no-op (no reclassify storm).
   */
  setGlobalThrottled(value: boolean, reason: string): void {
    if (this.globalThrottled === value) return;
    this.globalThrottled = value;
    // Reclassify every attached peer so their `throttled` modifier flips.
    // Each reclassify is independent; if a peer's lifecycle/modifiers
    // didn't change, no event fires.
    for (const handle of this.handles.values()) {
      this.reclassify(handle.peer, `global-throttled-changed:${reason}`);
    }
  }

  /**
   * Set the connection type for a specific peer (direct vs relay vs
   * unknown). Called by the relay-poll timer after `getStats()` resolves
   * the selected candidate pair. Exposed publicly for tests and for
   * future transport-driven hints.
   */
  setConnectionType(
    peerId: string,
    type: 'direct' | 'relay' | 'unknown',
  ): void {
    const prev = this.connectionTypes.get(peerId);
    if (prev === type) return;
    this.connectionTypes.set(peerId, type);
    const handle = this.handles.get(peerId);
    if (handle) {
      this.reclassify(handle.peer, `connection-type-changed:${prev ?? 'unknown'}->${type}`);
    }
  }

  /** Read the current node-level throttle flag. */
  getGlobalThrottled(): boolean {
    return this.globalThrottled;
  }

  /**
   * Permanently dispose the store: detach all peers, stop pollers,
   * unsubscribe from the pressure service. Used in tests; production
   * code typically uses the singleton for the app's lifetime.
   */
  dispose(): void {
    for (const peerId of [...this.handles.keys()]) {
      this.detach(peerId, 'store-dispose');
    }
    this.stopPollers();
    this.pressureUnsubscribe?.();
    this.pressureUnsubscribe = null;
    this.emitter.removeAllListeners();
  }

  /**
   * Reset all in-memory state. Used by tests; production code should call
   * `detach()` per-peer instead. Does NOT remove listeners on attached peers
   * — the caller is responsible for detaching first.
   */
  _resetForTest(): void {
    this.snapshots.clear();
    this.handles.clear();
    this.connectionTypes.clear();
    this.globalThrottled = false;
    this.stopPollers();
    this.emitter.removeAllListeners();
  }

  // ─── Internals ─────────────────────────────────────────────────────────────

  private buildClassifierInputs(peer: PeerLike) {
    const offlineSet = peer.node?.offlinePeers;
    const isOfflineInPresence =
      offlineSet instanceof Set ? offlineSet.has(peer.address) : false;

    return {
      rtcState: peer.connection.connectionState,
      iceState: peer.connection.iceConnectionState,
      isOfflineInPresence,
      retryPhase: peer.retryPhase,
      reconnectionAttempts: peer.reconnectionAttempts,
      chatChannelOpen: peer.chat.channel?.readyState === "open",
      // Populated by the stats poller — defaults to 'unknown' until the
      // first getStats() resolves the selected candidate pair type.
      connectionType: this.connectionTypes.get(peer.address) ?? 'unknown',
      isContactDeleted: peer.isContactDeleted ?? false,
      peerClosed: peer.closed ?? false,
      // PR-2 modifier inputs:
      activeTransferCount: peer.activeTransfers?.size ?? 0,
      isThrottled: this.globalThrottled,
    };
  }

  private logToDiagnostics(evt: PeerStateChangeEvent): void {
    connectionDiagnostics.logInfo(evt.peerId, "peer-lifecycle-state-change", {
      from: evt.from,
      to: evt.to,
      reason: evt.reason,
    });
  }

  // ─── Pollers (PR-2) ────────────────────────────────────────────────────────
  //
  // One self-rescheduling timer:
  //   - transferPoll: 1s while ANY peer has active transfers, 5s while all
  //     peers are idle. Sweeps every attached peer and reclassifies so
  //     `activeTransferCount` changes surface in the lifecycle modifiers
  //     within the chosen cadence. Polling is preferred over instrumenting
  //     MobyPeer's 50+ `activeTransfers.set/delete` call sites; reclassify
  //     is a no-op when nothing changed.
  //
  // The previous dedicated 5s `statsPoll` for connectionType has been
  // removed — `MobyPeer.updateConnectionStats` now feeds the result of its
  // existing adaptive 5s/30s `getStats()` call into the store via
  // `setConnectionType()`. This avoids running two separate getStats poll
  // loops per connected peer (which both contended on the network thread).
  //
  // Pollers shut down when the last peer detaches (no idle background work).

  private ensurePollersRunning(): void {
    if (!this.transferPollTimer) {
      this.scheduleNextTransferPoll();
    }
  }

  private stopPollers(): void {
    if (this.transferPollTimer) {
      clearTimeout(this.transferPollTimer);
      this.transferPollTimer = null;
    }
  }

  /**
   * Schedule the next transfer-poll tick. The cadence is adaptive: 1s
   * while any attached peer has active transfers, 5s while all are idle.
   * The self-rescheduling setTimeout chain replaces a fixed-rate
   * setInterval so idle peers don't trigger work every second.
   */
  private scheduleNextTransferPoll(): void {
    // Compute cadence based on whether any peer is mid-transfer.
    let anyBusy = false;
    for (const handle of this.handles.values()) {
      if ((handle.peer.activeTransfers?.size ?? 0) > 0) {
        anyBusy = true;
        break;
      }
    }
    const interval = anyBusy
      ? PeerStateStore.TRANSFER_POLL_BUSY_MS
      : PeerStateStore.TRANSFER_POLL_IDLE_MS;

    // Capture our own timer ID — only reschedule if we're still the
    // canonical timer (avoids double-rescheduling if stop+start race the
    // tick, mirroring the pattern in MobyPeer.startStatsCollection).
    const myTimerId: ReturnType<typeof setTimeout> = setTimeout(() => {
      this.pollTransferCounts();
      if (this.transferPollTimer === myTimerId) {
        this.scheduleNextTransferPoll();
      }
    }, interval);
    this.transferPollTimer = myTimerId;
  }

  /**
   * Reclassify every attached peer. Each reclassify is idempotent on
   * no-change so the cost is just a Map.get + classifier function call
   * per peer per tick.
   */
  private pollTransferCounts(): void {
    for (const handle of this.handles.values()) {
      this.reclassify(handle.peer, 'transfer-poll');
    }
  }
}

/**
 * Inspect an RTCPeerConnection's stats report and determine whether the
 * selected candidate pair uses a relay (TURN). Returns:
 *   - 'relay'   if either local or remote candidate type is 'relay'
 *   - 'direct'  if both endpoints are non-relay (host/srflx/prflx)
 *   - 'unknown' if no candidate pair is selected (yet)
 *   - null      if the stats report had no candidate-pair entries at all
 *
 * Exported separately so tests can pass a synthetic stats report.
 */
export async function detectCandidatePairType(
  connection: Pick<RTCPeerConnection, 'getStats'>,
): Promise<'direct' | 'relay' | 'unknown' | null> {
  const stats = await connection.getStats();
  return extractCandidatePairTypeFromStats(stats);
}

/**
 * Synchronous variant that operates on an already-fetched RTCStatsReport.
 * Used by `MobyPeer.updateConnectionStats` to feed `connectionType` into the
 * lifecycle store without paying for a second `getStats()` round-trip — the
 * 5s adaptive stats poll already has the report in hand. This keeps the
 * lifecycle store's `connectionType` event-driven from the existing path
 * instead of running a duplicate poll timer.
 */
export function extractCandidatePairTypeFromStats(
  stats: RTCStatsReport,
): 'direct' | 'relay' | 'unknown' | null {
  // Minimal candidate-stat shape — we only read `candidateType` / ids.
  // `RTCIceCandidateStats` isn't declared in every TS DOM lib version,
  // so we inline the fields we need rather than importing it.
  type CandidateStat = {
    id: string;
    type: 'local-candidate' | 'remote-candidate';
    candidateType?: 'host' | 'srflx' | 'prflx' | 'relay' | string;
  };
  type CandidatePairStat = {
    id: string;
    type: 'candidate-pair';
    localCandidateId?: string;
    remoteCandidateId?: string;
    selected?: boolean;
    nominated?: boolean;
  };

  let selectedPair: CandidatePairStat | null = null;
  const localCandidates = new Map<string, CandidateStat>();
  const remoteCandidates = new Map<string, CandidateStat>();
  let hasAnyPair = false;

  stats.forEach((stat: { type: string; id: string } & Record<string, unknown>) => {
    if (stat.type === 'candidate-pair') {
      hasAnyPair = true;
      const pair = stat as unknown as CandidatePairStat;
      // `nominated` + `selected` flags vary by browser — Firefox uses
      // `selected`, Chrome uses `nominated`. Take the first match.
      if (pair.selected || pair.nominated) {
        selectedPair = pair;
      }
    } else if (stat.type === 'local-candidate') {
      localCandidates.set(stat.id, stat as unknown as CandidateStat);
    } else if (stat.type === 'remote-candidate') {
      remoteCandidates.set(stat.id, stat as unknown as CandidateStat);
    }
  });

  if (!hasAnyPair) return null;
  if (!selectedPair) return 'unknown';

  const pair: CandidatePairStat = selectedPair;
  const local = localCandidates.get(pair.localCandidateId ?? '');
  const remote = remoteCandidates.get(pair.remoteCandidateId ?? '');

  const localType = local?.candidateType ?? 'unknown';
  const remoteType = remote?.candidateType ?? 'unknown';

  if (localType === 'relay' || remoteType === 'relay') return 'relay';
  if (localType === 'unknown' && remoteType === 'unknown') return 'unknown';
  return 'direct';
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function modifiersEqual(a: PeerModifiers, b: PeerModifiers): boolean {
  return (
    a.relay === b.relay &&
    a.localBusy === b.localBusy &&
    a.remoteBusy === b.remoteBusy &&
    a.throttled === b.throttled &&
    a.relayPath.length === b.relayPath.length &&
    a.relayPath.every((x, i) => b.relayPath[i] === x)
  );
}

function modifierValueEqual(
  a: boolean | readonly string[],
  b: boolean | readonly string[],
): boolean {
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((x, i) => b[i] === x);
  }
  return a === b;
}

// ─── Singleton + devtools hook ───────────────────────────────────────────────

export const peerStateStore = new PeerStateStore();

if (typeof window !== "undefined") {
  // Mirrors the `connectionDiagnostics` window-attach pattern.
  (window as unknown as { peerStateStore?: PeerStateStore }).peerStateStore =
    peerStateStore;
}
